import React, { useCallback, useState } from "react";
import styled from "styled-components";
import { AnimatePresence } from "framer-motion";
import { IoMdClose } from "react-icons/io";
import { FaCheck } from "react-icons/fa";
import { useContactStore } from "store";
import { ISiteContact } from "utils/types";
import { routeNames } from "utils";
import { useBreakpoint } from "hooks/media/useBreakpoint";
import { Modal } from "antd";

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.5rem;
  color: #333;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Title = styled.h2`
  margin-bottom: 1.5rem;
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
`;

const Form = styled.form`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  color: #666;

  &::after {
    content: "*";
    color: #ff0000;
    margin-left: 2px;
  }
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: #5575f7;
  }
`;

const FullWidthInput = styled(InputWrapper)`
  grid-column: 1 / -1;
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  resize: vertical;
  min-height: 100px;
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: #5575f7;
  }
`;

const RequiredInfo = styled.div`
  grid-column: 1 / -1;
  font-size: 0.75rem;
  color: #666;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &::before {
    content: "•";
    color: #000;
  }
`;

const Button = styled.button`
  grid-column: 1 / -1;
  background: #5575f7;
  color: white;
  border: none;
  border-radius: 100px;
  padding: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  box-shadow: 0px 16px 40px -12px #5575f766;

  &:hover {
    background: #4a67e3;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const SuccessIcon = styled.div`
  width: 48px;
  height: 48px;
  background: #4caf50;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  color: white;
  font-size: 24px;
`;

const SuccessMessage = styled.p`
  margin-bottom: 1.5rem;
  color: #333;
  text-align: center;
`;

const SuccessButton = styled(Button)`
  width: 100%;
  margin-top: 1rem;
`;

export interface ContactModelProps {
  onCreateContactMessage: (contactInfo: Partial<ISiteContact>) => Promise<boolean>
}
export const ContactModal: React.FC<ContactModelProps> = ({
  onCreateContactMessage
}) => {
  const {
    isOpen,
    closeModal,
  } = useContactStore();
  const [isLoading, setLoading] = useState(false)
  const [isSuccess, setSuccess] = useState(false)
  const [formData, updateFormData] = useState<Partial<ISiteContact>>({
    fullName: "", email: "", phoneNumber: "", reason: "", 
    message: ""
  })

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if(await onCreateContactMessage(formData)){
      setSuccess(true);
    }
    setLoading(false);
  }, [JSON.stringify(formData)])

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    updateFormData( data => ({...data, [e.target.name as keyof ISiteContact]: e.target.value }));
  };

  const { isMobile } = useBreakpoint();

  const Cover = isMobile ? FullWidthInput : InputWrapper

  return (
    <AnimatePresence>
      {isOpen && (
        // <Overlay
        //   initial={{ opacity: 0 }}
        //   animate={{ opacity: 1 }}
        //   exit={{ opacity: 0 }}
        //   onClick={() => closeModal()}
        // >
          <Modal
            open={isOpen}
            onCancel={closeModal}
            closable={false}
            footer={null}
            style={{ padding: 10 }}
            bodyProps={{
              style: {
                padding: 20
              }
            }}

          >
            {!isSuccess ? (
              <>
                <CloseButton onClick={() => closeModal()}>
                  <IoMdClose />
                </CloseButton>
                <Title>Contact us</Title>
                <Form onSubmit={handleSubmit}>
                  <FullWidthInput>
                    <Label>First name</Label>
                    <Input
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required
                    />
                  </FullWidthInput>
                  <Cover>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </Cover>
                  <Cover>
                    <Label>Phone number</Label>
                    <Input
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                    />
                  </Cover>
                  <FullWidthInput>
                    <Label>Subject</Label>
                    <Input
                      name="reason"
                      value={formData.reason}
                      onChange={handleInputChange}
                      required
                    />
                  </FullWidthInput>
                  <FullWidthInput>
                    <Label>Message</Label>
                    <TextArea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                    />
                  </FullWidthInput>
                  <RequiredInfo>Required information (*)</RequiredInfo>
                  <Button type="submit" disabled={isLoading}>
                    {isLoading ? "Please wait..." : "Send message"}
                  </Button>
                </Form>
              </>
            ) : (
              <>
                <CloseButton onClick={() => closeModal()}>
                  <IoMdClose />
                </CloseButton>
                <SuccessIcon>
                  <FaCheck />
                </SuccessIcon>
                <SuccessMessage>
                  Thank you for contacting us! Our team will get back to you
                  within 24 hours. Meanwhile, why not take the first step toward
                  your career journey?
                </SuccessMessage>
                <SuccessButton
                  onClick={() => (window.location.href =  routeNames.signUp)}
                >
                  Explore courses for free
                </SuccessButton>
              </>
            )}
          </Modal>
        // </Overlay>
      )}
    </AnimatePresence>
  );
};
